/**
 * API Configuration
 *
 * WHY: Centralize API-related constants and configurations
 * WHAT: Defines API endpoints, headers, and request configurations
 * BENEFIT: Easy to modify API settings in one place
 */

import { envConfig } from "./env.config";

// =======================
// 🔹 API Endpoints
// =======================

/**
 * API endpoint paths
 * WHY: Avoid hardcoding URLs throughout the app
 * USAGE: `${apiConfig.baseURL}${API_ENDPOINTS.auth.login}`
 */

export const API_ENDPOINTS = {
  // Authentication
  auth: {
    login: "/signin",
    adminLogin: "/signin/admin",
    logout: "/logout",
    refresh: "/auth/refresh",
    verifyLoginToken: "/verify-token",
    me: "/me",
    checkSession: "/check-session",
  },
  // Users
  users: {
    base: "/users",
    detail: (id: string | number) => `/users/${id}`,
    updateStatus: (id: string | number) => `/users/${id}/status`,
  },
  payments: {
    base: "/payments",
    createIntent: `/payments/create-intent`,
    paymentStatus: (paymentIntentId: string) =>
      `/payments/status/${paymentIntentId}`,
  },
  // add more as you want
} as const;

// =======================
// 🔹 API Configuration
// =======================

export const apiConfig = {
  // Base Url from environment
  baseUrl: envConfig.api.baseURL,
  //Request timeout (milliseconds)
  timeout: envConfig.api.timeout,

  // Default Headers for all request
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  retry: {
    maxRetries: 3,
    retryDelay: 500,
    retryableStatuses: [408, 429, 500, 502, 503, 504],
  },
  token: {
    storageKey: "access_token",
    refreshStorageKey: "refresh_token",
    headerKey: "Authorization",
    prefix: "Bearer",
  },
  // Pagination Defaults
  pagination: {
    defaultPage: 1,
    defaultPageSize: 10,
    maxPageSize: 1000,
  },
} as const;

export type ApiConfig = typeof apiConfig;
